import React from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Target, Phone } from 'lucide-react';

const AnalyticsCharts = () => {
  // Hourly call volume data
  const hourlyData = [
    { hour: '9 AM', calls: 12, booked: 3 },
    { hour: '10 AM', calls: 18, booked: 5 },
    { hour: '11 AM', calls: 24, booked: 7 },
    { hour: '12 PM', calls: 15, booked: 4 },
    { hour: '1 PM', calls: 20, booked: 6 },
    { hour: '2 PM', calls: 32, booked: 11 },
    { hour: '3 PM', calls: 38, booked: 13 },
    { hour: '4 PM', calls: 28, booked: 9 },
    { hour: '5 PM', calls: 22, booked: 6 },
    { hour: '6 PM', calls: 18, booked: 4 },
    { hour: '7 PM', calls: 12, booked: 3 },
    { hour: '8 PM', calls: 8, booked: 1 },
  ];

  // Conversion funnel data
  const funnelData = [
    { stage: 'Calls Made', value: 247, percentage: 100 },
    { stage: 'Connected', value: 168, percentage: 68 },
    { stage: 'Interested', value: 89, percentage: 36 },
    { stage: 'Booked', value: 42, percentage: 17 },
  ];

  // Campaign performance data
  const campaignData = [
    { campaign: 'Summer Sale', calls: 450, booked: 42, rate: 9.3 },
    { campaign: 'Trade-In', calls: 320, booked: 28, rate: 8.8 },
    { campaign: 'Service', calls: 580, booked: 15, rate: 2.6 },
    { campaign: 'Financing', calls: 200, booked: 0, rate: 0 },
  ];

  // Disposition pie chart data
  const dispositionData = [
    { name: 'Booked', value: 142, color: 'hsl(var(--accent))' },
    { name: 'Follow-up', value: 89, color: 'hsl(var(--chart-2))' },
    { name: 'Not Interested', value: 67, color: 'hsl(var(--chart-4))' },
    { name: 'Voicemail', value: 94, color: 'hsl(var(--chart-3))' },
    { name: 'No Answer', value: 128, color: 'hsl(var(--muted))' },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-charts">
      {/* Hourly Activity Chart */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold">Hourly Call Activity</h3>
          <p className="text-sm text-muted-foreground mt-1">Call volume and bookings by hour</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={hourlyData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <XAxis dataKey="hour" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="calls"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              name="Calls Made"
            />
            <Line
              type="monotone"
              dataKey="booked"
              stroke="hsl(var(--accent))"
              strokeWidth={2}
              name="Booked"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold">Conversion Funnel</h3>
            <p className="text-sm text-muted-foreground mt-1">Today's journey from call to booking</p>
          </div>
          <div className="space-y-3">
            {funnelData.map((stage, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{stage.stage}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{stage.value}</span>
                    <span className="text-xs text-muted-foreground">({stage.percentage}%)</span>
                  </div>
                </div>
                <div className="h-8 bg-secondary rounded-lg overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-accent flex items-center justify-end pr-3 text-primary-foreground text-sm font-medium transition-all"
                    style={{ width: `${stage.percentage}%` }}
                  >
                    {stage.percentage >= 20 && `${stage.value}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Disposition Pie Chart */}
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold">Call Outcomes</h3>
            <p className="text-sm text-muted-foreground mt-1">Distribution of call dispositions</p>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={dispositionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {dispositionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Campaign Performance Bar Chart */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold">Campaign Performance Comparison</h3>
          <p className="text-sm text-muted-foreground mt-1">Total calls and booking rate by campaign</p>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={campaignData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
            <XAxis dataKey="campaign" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Bar dataKey="calls" fill="hsl(var(--primary))" name="Total Calls" />
            <Bar dataKey="booked" fill="hsl(var(--accent))" name="Booked" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AnalyticsCharts;
